import { isDoNotTrackEnabled } from '../config/metrics';

export const ANALYTICS_CHOICE_KEY = 'noesa.analytics.consent.v1';
export const ANALYTICS_CHOICE_EVENT = 'noesa:analytics-consent';
export const TOUCH_KEY = 'noesa.measurement.touch.v2';
export const TOUCH_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const LEGACY_TOUCH_KEY = 'noesa.measurement.touch.v1';
const LANDINGS = new Set([
  '/', '/welcome', '/ai-assistant', '/ai-photo-editor', '/ai-planner', '/ai-diary', '/code',
  '/ai-for-work', '/ai-for-productivity', '/ai-for-students', '/mobile', '/pricing', '/about',
]);
const TAG_KEYS = ['source', 'medium', 'campaign', 'content', 'term'];
const DETAIL_KEYS = ['campaign_id', 'adgroup_id', 'ad_id', 'topic_id', 'source_type', 'match_type'];
const validTag = value => typeof value === 'string' && /^[A-Za-z0-9_.-]{1,96}$/.test(value);
const validTerm = value => typeof value === 'string' && /^[\p{L}\p{N} _+().-]{1,160}$/u.test(value) && !/[0-9][0-9 ()+-]{7,}/.test(value);
const validValue = (key, value) => key === 'term' ? validTerm(value) : key.endsWith('_id') && key !== 'topic_id' ? typeof value === 'string' && /^[0-9]{1,32}$/.test(value) : validTag(value);

function landingPath(pathname) {
  const normalized = pathname.replace(/^\/en(?=\/|$)/, '').replace(/\/+$/, '') || '/';
  const path = normalized === '/ai-calendar' ? '/ai-planner' : normalized;
  return LANDINGS.has(path) ? path : undefined;
}
function sanitize(saved, now, ttl) {
  if (!saved || !Number.isFinite(saved.at) || now - saved.at > ttl || saved.at > now) return null;
  const touch = { at: saved.at };
  for (const key of [...TAG_KEYS, ...DETAIL_KEYS]) if (validValue(key, saved[key])) touch[key] = saved[key];
  if (!['source', 'medium', 'campaign'].some(key => touch[key])) return null;
  if (LANDINGS.has(saved.landing)) touch.landing = saved.landing;
  return touch;
}
function clearTouch() {
  try { localStorage.removeItem(TOUCH_KEY); } catch {}
  try { sessionStorage.removeItem(LEGACY_TOUCH_KEY); } catch {}
}
function saveTouch(touch) {
  try { localStorage.setItem(TOUCH_KEY, JSON.stringify(touch)); } catch { /* Current request still works. */ }
}
function readTouch(now) {
  try {
    const saved = sanitize(JSON.parse(localStorage.getItem(TOUCH_KEY) || 'null'), now, TOUCH_TTL_MS);
    if (saved) return saved;
  } catch {}
  try {
    // Only migrate a still-valid old touch; never revive expired history.
    const legacy = sanitize(JSON.parse(sessionStorage.getItem(LEGACY_TOUCH_KEY) || 'null'), now, 30 * 60 * 1000);
    if (legacy) { saveTouch(legacy); sessionStorage.removeItem(LEGACY_TOUCH_KEY); return legacy; }
  } catch {}
  clearTouch();
  return null;
}
export function captureMeasurementTouch() {
  if (!acquisitionAllowed()) { clearTouch(); return null; }
  const now = Date.now();
  const saved = readTouch(now);
  if (saved) return saved; // First known source; subsequent campaigns do not renew expiry.
  try {
    const url = new URL(window.location.href);
    const landing = landingPath(url.pathname);
    if (!landing) return null;
    const touch = { at: now, landing };
    for (const key of TAG_KEYS) {
      const value = url.searchParams.get('utm_' + key);
      if (validValue(key, value)) touch[key] = value;
    }
    for (const key of DETAIL_KEYS) {
      const value = url.searchParams.get(key);
      if (validValue(key, value)) touch[key] = value;
    }
    // Existing ads already expand the numeric ad ID in utm_content.
    const expandedId = (touch.content || '').match(/_([0-9]{1,32})$/);
    if (!touch.ad_id && expandedId) touch.ad_id = expandedId[1];
    if (!['source', 'medium', 'campaign'].some(key => touch[key])) return null;
    saveTouch(touch);
    return touch;
  } catch { return null; }
}
export function analyticsAllowed() { return acquisitionAllowed(); }
export function hasLegacyAnalyticsConsent() {
  try { return !isDoNotTrackEnabled() && localStorage.getItem(ANALYTICS_CHOICE_KEY) === 'granted'; }
  catch { return false; }
}
export function acquisitionAllowed() {
  try {
    const choice = localStorage.getItem(ANALYTICS_CHOICE_KEY);
    return !isDoNotTrackEnabled() && (choice === null || choice === 'granted');
  } catch { return false; }
}
export function setAnalyticsChoice(granted) {
  try {
    localStorage.setItem(ANALYTICS_CHOICE_KEY, granted ? 'granted' : 'denied');
    if (!granted) clearTouch();
    if (granted) captureMeasurementTouch();
    window.dispatchEvent(new Event(ANALYTICS_CHOICE_EVENT));
  } catch {}
}
export function measurementHeaders() {
  const headers = { 'X-Noesa-Platform': 'web' };
  if (process.env.NODE_ENV !== 'production') headers['X-Noesa-Build-Channel'] = 'development';
  const version = process.env.REACT_APP_RELEASE_VERSION;
  if (/^[0-9][0-9A-Za-z.+-]{0,39}$/.test(version || '')) headers['X-Noesa-App-Version'] = version;
  if (analyticsAllowed()) headers['X-Noesa-Measurement'] = 'first-party-v1';
  try {
    const touch = captureMeasurementTouch();
    if (!touch) return headers;
    headers['X-Noesa-Attribution'] = 'registration-utm-v2';
    for (const key of TAG_KEYS) if (validValue(key, touch[key])) headers['X-Noesa-Utm-' + key] = key === 'term' ? encodeURIComponent(touch[key]) : touch[key];
    for (const key of DETAIL_KEYS) if (validValue(key, touch[key])) headers['X-Noesa-Acquisition-' + key] = touch[key];
    headers['X-Noesa-Acquisition-at'] = String(touch.at);
    if (LANDINGS.has(touch.landing)) headers['X-Noesa-Landing'] = touch.landing;
  } catch { /* Never send raw URLs, referrers, form contents or fingerprints. */ }
  return headers;
}
